import React, {forwardRef, useContext, useEffect, useMemo, useState} from 'react';
import {MaterialContext, Panel, PanelProps} from '@wonderlandengine/react-ui/components';

import {Object3D, Texture} from '@wonderlandengine/api';
import {Color, parseColor} from '../utils/utils.js';
import {FlatOpaqueTextured} from '../../types/materials.js';

const materialPool = new Map<string, FlatOpaqueTextured>();
/**
 * Gets or creates a material from the pool with the given texture and color
 */
const getMaterialFromPool = (
    context: React.ContextType<typeof MaterialContext>,
    texture: Texture,
    color: Color
): FlatOpaqueTextured => {
    // Create a unique ID for this texture and color combination
    const parsedColor = parseColor(color);
    const materialId = `${texture.index}_${parsedColor}`;

    // Check if we already have this material
    if (materialPool.has(materialId)) {
        return materialPool.get(materialId)!;
    }

    // Create a new material
    const newMaterial = context.panelMaterialTextured?.clone() as FlatOpaqueTextured;
    newMaterial.setFlatTexture(texture);
    newMaterial.setColor(parsedColor);

    // Store in the pool
    materialPool.set(materialId, newMaterial);
    return newMaterial;
};

export const ColoredImage = forwardRef<
    Object3D,
    React.PropsWithChildren<{src: Texture; color: Color} & PanelProps>
>((props, ref) => {
    const context = useContext(MaterialContext);
    const [material, setMaterial] = useState<FlatOpaqueTextured | null>(null);

    // Handle texture loading and material creation
    useEffect(() => {
        const loadTexture = async () => {
            try {
                const pooledMaterial = getMaterialFromPool(context, props.src, props.color);
                setMaterial(pooledMaterial);
            } catch (error) {
                console.error('Failed to load texture:', error);
            }
        };

        loadTexture();
    }, [props.src, props.color, context]);

    // Don't render until material is ready
    if (!material) {
        return null;
    }
    return (
        <Panel rounding={0} {...props} material={material} ref={ref}>
            {props.children}
        </Panel>
    );
});
